import { config } from '@/config/env';
import { supplierAuthService } from '@/features/proveedores/api/supplierAuthService';
import type { SupplierPayment, SupplierPaymentStats, SupplierPaymentFilters, SupplierInvoice, SupplierPaymentRaw } from '@/types/supplierPayment.types';

class SupplierPaymentsService {
  private getAuthHeaders() {
    const token = supplierAuthService.getToken();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  // Obtener todas las facturas de una orden
  private async getInvoices(orderId: string): Promise<SupplierInvoice[]> {
    const response = await fetch(
      `${config.apiBaseUrl}/suppliers/purchase-orders/${orderId}/invoices`,
      { headers: this.getAuthHeaders() }
    );

    if (!response.ok) {
      throw new Error('Error al obtener facturas');
    }

    return await response.json();
  }

  // Obtener pagos de una factura específica
  private async getPaymentsByInvoice(orderId: string, invoiceId: string): Promise<SupplierPaymentRaw[]> {
    const response = await fetch(
      `${config.apiBaseUrl}/suppliers/purchase-orders/${orderId}/invoices/${invoiceId}/payments`,
      { headers: this.getAuthHeaders() }
    );

    if (!response.ok) {
      throw new Error('Error al obtener pagos');
    }

    return await response.json();
  }

  // Obtener todos los pagos de una orden (consolidado)
  async getAllPaymentsByOrder(orderId: string, filters?: SupplierPaymentFilters): Promise<SupplierPayment[]> {
    try {
      // 1. Obtener todas las facturas
      const invoices = await this.getInvoices(orderId);

      // 2. Obtener pagos de todas las facturas en paralelo
      const paymentsPromises = invoices.map(invoice =>
        this.getPaymentsByInvoice(orderId, invoice.id)
          .then(payments => ({ invoice, payments }))
          .catch(() => ({ invoice, payments: [] })) // Si falla, devolver array vacío
      );

      const invoicesWithPayments = await Promise.all(paymentsPromises);

      // 3. Consolidar y enriquecer todos los pagos
      const allPayments: SupplierPayment[] = [];

      for (const { invoice, payments } of invoicesWithPayments) {
        const enrichedPayments = payments.map(payment => ({
          id: payment.id,
          invoice_id: invoice.id,
          order_id: orderId,
          invoice_number: invoice.invoice_number,
          invoice_total: invoice.invoice_total,
          invoice_status: invoice.status,
          amount: payment.amount,
          payment_date: payment.payment_date,
          payment_method: payment.payment_method,
          reference_number: payment.reference_number,
          notes: payment.notes || '',
          complement_xml_url: payment.complement_xml_url || '',
          complement_pdf_url: payment.complement_pdf_url || '',
          complement_status: payment.complement_status || 'pending',
          complement_validation_notes: payment.complement_validation_notes,
          waiting_days: this.calculateWaitingDays(payment.created_at),
          status: invoice.status === 'validated' ? 'paid' as const : 'pending' as const,
          created_at: payment.created_at,
        }));

        allPayments.push(...enrichedPayments);
      }

      // 4. Aplicar filtros
      return this.applyFilters(allPayments, filters);
    } catch (error) {
      console.error('Error al obtener pagos:', error);
      throw error;
    }
  }

  // Aplicar filtros a los pagos
  private applyFilters(payments: SupplierPayment[], filters?: SupplierPaymentFilters): SupplierPayment[] {
    let filtered = [...payments];

    if (filters?.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(p =>
        p.invoice_number.toLowerCase().includes(search) ||
        p.reference_number.toLowerCase().includes(search) ||
        p.payment_method.toLowerCase().includes(search)
      );
    }

    if (filters?.payment_method) {
      filtered = filtered.filter(p => p.payment_method === filters.payment_method);
    }

    if (filters?.status) {
      filtered = filtered.filter(p => p.status === filters.status);
    }

    if (filters?.from_date) {
      filtered = filtered.filter(p =>
        new Date(p.payment_date) >= new Date(filters.from_date!)
      );
    }

    if (filters?.to_date) {
      filtered = filtered.filter(p =>
        new Date(p.payment_date) <= new Date(filters.to_date!)
      );
    }

    return filtered;
  }

  // Calcular días de espera
  private calculateWaitingDays(createdAt: string): number {
    const created = new Date(createdAt);
    const now = new Date();
    const diff = now.getTime() - created.getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  }

  // Calcular estadísticas de pagos
  calculateStats(payments: SupplierPayment[]): SupplierPaymentStats {
    const pending = payments.filter(p => p.status === 'pending');
    const paid = payments.filter(p => p.status === 'paid');

    // Obtener facturas únicas
    const uniqueInvoices = new Set(payments.map(p => p.invoice_id));

    // Calcular promedio de días de espera
    const avgDays = payments.length > 0
      ? payments.reduce((sum, p) => sum + p.waiting_days, 0) / payments.length
      : 0;

    return {
      totalPayments: payments.length,
      totalInvoices: uniqueInvoices.size,
      paidAmount: paid.reduce((sum, p) => sum + p.amount, 0),
      pendingAmount: pending.reduce((sum, p) => sum + p.amount, 0),
      averagePaymentDays: Math.round(avgDays),
    };
  }

  // Formatear fecha
  // Una fecha sin hora (YYYY-MM-DD) pasada por new Date() se lee como
  // medianoche UTC y en Mexico sale el dia anterior: un pago del 10/08 se
  // mostraba 09/08. El mensaje de bloqueo por complemento si lo hacia bien, asi
  // que las dos pantallas se contradecian sobre la misma fecha.
  formatDate(dateString: string): string {
    const soloFecha = /^\d{4}-\d{2}-\d{2}/.exec(dateString || '');
    if (soloFecha) {
      const [anio, mes, dia] = soloFecha[0].split('-');
      return `${dia}/${mes}/${anio}`;
    }
    const date = new Date(dateString);
    return date.toLocaleDateString('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  // Formatear moneda
  formatCurrency(amount: number): string {
    return `$${amount.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  // Subir complemento de pago
  async uploadComplement(
    orderId: string,
    paymentId: string,
    xmlFile: File,
    pdfFile?: File
  ): Promise<void> {
    const formData = new FormData();
    formData.append('xml', xmlFile);
    if (pdfFile) {
      formData.append('pdf', pdfFile);
    }

    const token = supplierAuthService.getToken();
    const response = await fetch(
      `${config.apiBaseUrl}/suppliers/purchase-orders/${orderId}/payments/${paymentId}/complement`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          // NO incluir Content-Type, el browser lo maneja automáticamente con FormData
        },
        body: formData,
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Error al subir el complemento');
    }
  }

  // Exportar a Excel (mock)
  async exportToExcel(_orderId: string): Promise<void> {
    // TODO: Implementar exportación real
    alert('Funcionalidad de exportación en desarrollo');
  }
}

export const supplierPaymentsService = new SupplierPaymentsService();
